import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import { TIPOS_VINO } from "@/lib/tastingOptions";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  nombre: "", bodega: "", pais: "", region: "", denominacion: "", anada: "",
  variedad: "", tipo: "", alcohol: "", volumen: "", precio: "", lugar_compra: "",
  fecha_compra: "", foto_botella: "", foto_etiqueta: "",
  cantidad_botellas: 1, estado_botella: "En Cava", anio_optimo_desde: "", anio_optimo_hasta: "",
};

const ESTADOS_BOTELLA = ["En Cava", "Abierta", "Terminada"];

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-grises-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function WineForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: paises = [] } = useQuery({ queryKey: ["cat", "paises"], queryFn: async () => (await api.get("/catalogs/paises")).data });
  const { data: bodegas = [] } = useQuery({ queryKey: ["cat", "bodegas"], queryFn: async () => (await api.get("/catalogs/bodegas")).data });
  const { data: regiones = [] } = useQuery({ queryKey: ["cat", "regiones"], queryFn: async () => (await api.get("/catalogs/regiones")).data });
  const { data: variedades = [] } = useQuery({ queryKey: ["cat", "variedades"], queryFn: async () => (await api.get("/catalogs/variedades")).data });
  const { data: denominaciones = [] } = useQuery({ queryKey: ["cat", "denominaciones"], queryFn: async () => (await api.get("/catalogs/denominaciones")).data });

  useEffect(() => {
    if (id) api.get(`/wines/${id}`).then((r) => { setForm({ ...EMPTY, ...r.data }); setLoaded(true); });
    else setLoaded(true);
  }, [id]);

  const save = async () => {
    if (!loaded) return;
    if (!form.nombre.trim()) { toast.error("El nombre del vino es obligatorio"); return; }
    setSaving(true);
    try {
      if (id) await api.put(`/wines/${id}`, form);
      else await api.post("/wines", form);
      qc.invalidateQueries({ queryKey: ["wines"] });
      qc.invalidateQueries({ queryKey: ["cat"] });
      toast.success(id ? "Vino actualizado" : "Vino registrado");
      navigate("/app/vinos");
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-up">
      <button data-testid="back-btn" onClick={() => navigate("/app/vinos")} className="flex items-center gap-1.5 text-sm text-grises-500 hover:text-borgona-700 mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <p className="text-xs uppercase tracking-[0.2em] text-dorado-700 mb-1">{id ? "Editar" : "Nuevo"}</p>
      <h1 className="font-serif text-4xl text-negro mb-8">{id ? "Editar vino" : "Registrar vino"}</h1>

      <Card className="p-6 sm:p-8 border-grises-200 rounded-xl space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Field label="Nombre del vino *">
              <Input data-testid="wine-nombre-input" value={form.nombre} onChange={(e) => set("nombre")(e.target.value)} placeholder="Ej. Vega Sicilia Único" className="h-11" />
            </Field>
          </div>
          <Field label="Bodega">
            <Input data-testid="wine-bodega-input" list="dl-bodegas" value={form.bodega} onChange={(e) => set("bodega")(e.target.value)} className="h-11" />
            <datalist id="dl-bodegas">{bodegas.map((x) => <option key={x} value={x} />)}</datalist>
          </Field>
          <Field label="Tipo de vino">
            <Select value={form.tipo} onValueChange={set("tipo")}>
              <SelectTrigger data-testid="wine-tipo-select" className="h-11"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>{TIPOS_VINO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="País">
            <Input data-testid="wine-pais-input" list="dl-paises" value={form.pais} onChange={(e) => set("pais")(e.target.value)} className="h-11" />
            <datalist id="dl-paises">{paises.map((x) => <option key={x} value={x} />)}</datalist>
          </Field>
          <Field label="Región">
            <Input data-testid="wine-region-input" list="dl-regiones" value={form.region} onChange={(e) => set("region")(e.target.value)} className="h-11" />
            <datalist id="dl-regiones">{regiones.map((x) => <option key={x} value={x} />)}</datalist>
          </Field>
          <Field label="Denominación de origen">
            <Input data-testid="wine-denominacion-input" list="dl-denominaciones" value={form.denominacion} onChange={(e) => set("denominacion")(e.target.value)} className="h-11" />
            <datalist id="dl-denominaciones">{denominaciones.map((x) => <option key={x} value={x} />)}</datalist>
          </Field>
          <Field label="Añada">
            <Input data-testid="wine-anada-input" value={form.anada} onChange={(e) => set("anada")(e.target.value)} placeholder="2019" className="h-11" />
          </Field>
          <Field label="Variedad de uva">
            <Input data-testid="wine-variedad-input" list="dl-variedades" value={form.variedad} onChange={(e) => set("variedad")(e.target.value)} className="h-11" />
            <datalist id="dl-variedades">{variedades.map((x) => <option key={x} value={x} />)}</datalist>
          </Field>
          <Field label="Alcohol (%)">
            <Input data-testid="wine-alcohol-input" value={form.alcohol} onChange={(e) => set("alcohol")(e.target.value)} placeholder="14.5" className="h-11" />
          </Field>
          <Field label="Volumen (ml)">
            <Input data-testid="wine-volumen-input" value={form.volumen} onChange={(e) => set("volumen")(e.target.value)} placeholder="750" className="h-11" />
          </Field>
          <Field label="Precio">
            <Input data-testid="wine-precio-input" value={form.precio} onChange={(e) => set("precio")(e.target.value)} placeholder="25.00" className="h-11" />
          </Field>
          <Field label="Lugar de compra">
            <Input data-testid="wine-lugar-input" value={form.lugar_compra} onChange={(e) => set("lugar_compra")(e.target.value)} className="h-11" />
          </Field>
          <Field label="Fecha de compra">
            <Input data-testid="wine-fecha-compra-input" type="date" value={form.fecha_compra} onChange={(e) => set("fecha_compra")(e.target.value)} className="h-11" />
          </Field>
          <Field label="Cantidad de botellas *">
            <Input data-testid="wine-cantidad-input" type="number" min="0" value={form.cantidad_botellas} onChange={(e) => set("cantidad_botellas")(Math.max(0, parseInt(e.target.value, 10) || 0))} className="h-11" />
          </Field>
          <Field label="Estado de la botella">
            <Select value={form.estado_botella} onValueChange={set("estado_botella")}>
              <SelectTrigger data-testid="wine-estado-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS_BOTELLA.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Año óptimo de consumo (desde)">
            <Input data-testid="wine-optimo-desde-input" value={form.anio_optimo_desde} onChange={(e) => set("anio_optimo_desde")(e.target.value)} placeholder="2025" className="h-11" />
          </Field>
          <Field label="Año óptimo de consumo (hasta)">
            <Input data-testid="wine-optimo-hasta-input" value={form.anio_optimo_hasta} onChange={(e) => set("anio_optimo_hasta")(e.target.value)} placeholder="2030" className="h-11" />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
          <ImageUpload label="Fotografía de la botella" value={form.foto_botella} onChange={set("foto_botella")} testId="upload-botella" />
          <ImageUpload label="Fotografía de la etiqueta" value={form.foto_etiqueta} onChange={set("foto_etiqueta")} testId="upload-etiqueta" />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate("/app/vinos")} className="rounded-full border-grises-300">Cancelar</Button>
          <Button data-testid="save-wine-btn" onClick={save} disabled={saving || !loaded}
            className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full transition-colors duration-300">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Guardando..." : "Guardar vino"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
